import { useState } from "react";
import type { FormEvent } from "react";

const FORM_SUBMIT_URL = "https://readdy.ai/api/form/dahg32rp14a1h3mr6lhg";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [count, setCount] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = String(value).trim();
      if (!text) return;
      payload.append(key, text);
    });

    try {
      setStatus("submitting");
      setFormError("");

      const response = await fetch(FORM_SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const code = parsed?.code;
      const messageText = typeof serverMsg === "string" ? serverMsg : "";
      const isSpam = messageText.toLowerCase().includes("spam");

      if (!response.ok || code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(messageText || "提交失败，请稍后重试。");
        return;
      }

      setStatus("success");
      setCount(0);
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div id="contact" className="scroll-mt-24 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
      <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
        线索投稿与联系
      </h2>
      <p className="mt-2 font-label text-sm leading-relaxed text-foreground-600">
        发现新的回应、原始截图，或看到我们哪一处理解得不对，都可以在这里告诉我们。信息越多，归档越准。
      </p>

      <form
        id="contact-lead-form"
        data-readdy-form
        onSubmit={handleSubmit}
        noValidate
        className="mt-6 space-y-5"
      >
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="name" className="font-label text-sm font-medium text-foreground-900">
              你的称呼 <span className="text-primary-600">*</span>
            </label>
            <input
              id="name"
              name="name"
              type="text"
              required
              placeholder="昵称或称呼"
              className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 font-label text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>

          <div>
            <label htmlFor="email" className="font-label text-sm font-medium text-foreground-900">
              联系邮箱 <span className="text-primary-600">*</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              placeholder="用于回复与核实"
              className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 font-label text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>
        </div>

        <div>
          <label htmlFor="topic" className="font-label text-sm font-medium text-foreground-900">
            事由类型 <span className="text-primary-600">*</span>
          </label>
          <select
            id="topic"
            name="topic"
            required
            defaultValue=""
            className="mt-2 w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-4 py-2.5 font-label text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            <option value="" disabled>
              请选择
            </option>
            <option value="提供新线索">提供新线索</option>
            <option value="补充回应原文">补充回应原文</option>
            <option value="指出说法出入">指出说法出入</option>
            <option value="合作与建议">合作与建议</option>
          </select>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label htmlFor="message" className="font-label text-sm font-medium text-foreground-900">
              详细说明 <span className="text-primary-600">*</span>
            </label>
            <span className="font-label text-xs text-foreground-400">{count}/500</span>
          </div>
          <textarea
            id="message"
            name="message"
            required
            maxLength={500}
            rows={5}
            onChange={(e) => setCount(e.target.value.length)}
            placeholder="简单描述事件、涉及的相关方，以及你能提供的原始材料（如截图链接）"
            className="mt-2 w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 font-label text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
          ></textarea>
        </div>

        <input
          type="text"
          name="website_alt"
          className="field-company-alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />

        {status === "error" && (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 font-label text-sm text-primary-800"
          >
            <i className="ri-error-warning-line mt-0.5 flex-none"></i>
            {formError || "提交失败，请稍后重试。"}
          </p>
        )}

        {status === "success" && (
          <p
            role="status"
            className="flex items-start gap-2 rounded-md border border-secondary-300 bg-secondary-50 px-4 py-3 font-label text-sm text-secondary-900"
          >
            <i className="ri-checkbox-circle-line mt-0.5 flex-none"></i>
            已收到你的信息，我们会尽快核实并回复。感谢你帮忙把说法补齐。
          </p>
        )}

        <button
          type="submit"
          disabled={status === "submitting"}
          className="flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        >
          {status === "submitting" ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              正在提交
            </>
          ) : (
            <>
              <i className="ri-send-plane-line"></i>
              提交信息
            </>
          )}
        </button>
      </form>
    </div>
  );
}